import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { numberToWords } from '../utilities/numberToWords';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { getOrderById, updateDispatchStatus } from '../utilities/api';

const OrderView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useRef(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [data, setData] = useState({
        header: null,
        items: [],
        taxSummary: [],
        outlet: null,
        customer: null
        // dispatch removed here
    });

    const isPending = data.header?.status?.toLowerCase() === 'pending';

    const fetchData = () => {
        setLoading(true);
        getOrderById(id)
            .then((res) => {
                const parsed = res.final_output ? JSON.parse(res.final_output) : res;
                setData({
                    header: parsed.header,
                    items: parsed.items || [],
                    taxSummary: parsed.tax_summary || [],
                    outlet: parsed.outlet,
                    customer: parsed.customer
                });
            })
            .catch((err) => {
                console.error('Fetch Error:', err);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    const handleDispatchOrder = async () => {
        setProcessing(true);

        const payload = {
            order_id: id,
            status: 'Dispatched',
            rolename: localStorage.getItem('username') || 'User',
            branch_id: localStorage.getItem('branchid') || '0',
            branchname: localStorage.getItem('branch') || 'Main'
        };

        try {
            const response = await updateDispatchStatus(payload);

            if (response.status === 'success') {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: response.message || 'Order Dispatched',
                    life: 2000
                });
                setTimeout(() => navigate(`/orderlist/`), 1500);
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Server Error',
                    detail: response.message || 'Update failed'
                });
            }
        } catch (err) {
            console.error('Dispatch Error:', err);
            toast.current.show({
                severity: 'error',
                summary: 'Connection Error',
                detail: 'Could not connect to server. Check your internet or API status.'
            });
        } finally {
            setProcessing(false);
        }
    };

    const confirmDispatch = () => {
        confirmDialog({
            message: 'Are you sure you want to mark this order as DISPATCHED?',
            header: 'Dispatch Confirmation',
            icon: 'pi pi-truck',
            acceptClassName: 'p-button-info',
            accept: () => handleDispatchOrder()
        });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    };

    const totals = useMemo(() => {
        return data.items.reduce(
            (acc, i) => ({
                taxable: acc.taxable + (parseFloat(i.item_taxable_amt) || 0),
                taxAmt: acc.taxAmt + (parseFloat(i.item_tax_amt) || 0),
                grand: acc.grand + (parseFloat(i.total_price) || 0)
            }),
            { taxable: 0, taxAmt: 0, grand: 0 }
        );
    }, [data.items]);

    const getFormattedWords = (amount) => {
        let words = numberToWords(Math.round(amount));
        const cleanWords = words.replace(/only/gi, '').trim();
        return `${cleanWords} Rupees Only`;
    };

    const handlePrint = () => {
        const content = document.getElementById('printable');
        if (!content) return;

        const printCSS = `
            @page { margin: 10mm; size: A4; }
            * { box-sizing: border-box; }
            body { margin: 0; padding: 0; font-family: Arial, sans-serif; font-size: 12px; color: #000; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; width: 100%; }
            .invoice-container { border: 1px solid #000; width: 100%; overflow: hidden; }
            .section-border { border-bottom: 1px solid #000; padding: 10px 15px; width: 100%; overflow: hidden; }
            .table-section { padding: 0; width: 100%; }
            .receipt-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
            .receipt-table th { border: 1px solid #000; padding: 8px 6px; font-size: 11px; text-align: center; }
            .receipt-table td { border: 1px solid #000; padding: 8px 6px; font-size: 11px; word-wrap: break-word; overflow-wrap: break-word; }
            .flex-row { display: flex; width: 100%; overflow: hidden; }
            .col-50 { width: 50%; padding: 10px 15px; box-sizing: border-box; min-width: 0; overflow: hidden; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .footer-text { padding: 10px; text-align: center; font-size: 10px; color: #555; font-style: italic; }
            h2 { font-size: 16px; margin: 2px 0; text-align: center; }
            h3 { font-size: 14px; margin: 3px 0; text-align: center; }
            h4 { font-size: 13px; margin: 3px 0; font-weight: 700; }
            p { margin: 3px 0; font-size: 12px; font-weight: 600; }
            small { font-size: 11px; }
            b { font-weight: 700; }
        `;

        let iframe = document.getElementById('receipt-print-frame');
        if (!iframe) {
            iframe = document.createElement('iframe');
            iframe.id = 'receipt-print-frame';
            Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0', visibility: 'hidden' });
            document.body.appendChild(iframe);
        }

        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.open();
        doc.write(`<!DOCTYPE html><html><head><title>Receipt</title><style>${printCSS}</style></head><body>${content.outerHTML}</body></html>`);
        doc.close();

        iframe.contentWindow.focus();
        setTimeout(() => iframe.contentWindow.print(), 300);
    };

    if (loading) return <div className="p-5 text-center">Loading Invoice...</div>;

    return (
        <div className="order-view-wrapper">
            <Toast ref={toast} />
            <ConfirmDialog />

            <style>{`
    .order-view-wrapper { background: #f0f2f5; min-height: 100vh; padding: 40px 20px; font-family: Inter, sans-serif; }
    .main-layout { display: flex; gap: 30px; max-width: 1200px; margin: 0 auto; align-items: flex-start; }
    .sidebar-options { width: 280px; background: #fff; padding: 25px; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.05); position: sticky; top: 40px; }
    .status-container { padding: 12px; border-radius: 6px; text-align: center; font-weight: 700; margin-bottom: 20px; border: 1px solid; font-size: 13px; text-transform: uppercase; }
    .status-pending { background: #fff9e6; color: #996a13; border-color: #ffeeb3; }
    .status-dispatched { background: #e6f7ff; color: #0050b3; border-color: #91d5ff; }
    .invoice-container { flex: 1; background: #fff; border: 1px solid #000; box-shadow: 0 10px 30px rgba(0,0,0,0.08); box-sizing: border-box; }
    .table-section { padding: 20px; }
    .section-border { border-bottom: 1px solid #000; padding: 15px 20px; box-sizing: border-box; }
    .receipt-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    .receipt-table th { background: #f8f9fa !important; border: 1px solid #000; padding: 10px 8px; font-size: 11px; }
    .receipt-table td { border: 1px solid #000; padding: 10px 8px; font-size: 13px; word-wrap: break-word; overflow-wrap: break-word; }
    .flex-row { display: flex; width: 100%; box-sizing: border-box; }
    .col-50 { width: 50%; box-sizing: border-box; min-width: 0; }
    .text-right { text-align: right; }
    .btn { width: 100%; padding: 14px; margin-bottom: 12px; cursor: pointer; border-radius: 8px; border: none; font-weight: 700; text-transform: uppercase; font-size: 12px; }
    .footer-text { padding: 15px; text-align: center; font-size: 11px; color: #555; font-style: italic; }
`}</style>

            <div className="main-layout">
                <aside className="sidebar-options no-print">
                    <div className={`status-container status-${data.header?.status?.toLowerCase()}`}>{data.header?.status}</div>

                    {isPending && (
                        <button className="btn" style={{ background: '#17a2b8', color: '#fff' }} onClick={confirmDispatch} disabled={processing}>
                            {processing ? 'Processing...' : 'Dispatch Order'}
                        </button>
                    )}

                    <button className="btn" style={{ background: '#28a745', color: '#fff' }} onClick={handlePrint}>
                        Print Receipt
                    </button>
                    <button className="btn" style={{ background: '#f0f0f0', color: '#333' }} onClick={() => navigate(-1)}>
                        Back
                    </button>
                </aside>

                <div className="invoice-container" id="printable">
                    <div className="section-border" style={{ textAlign: 'center' }}>
                        <h2 style={{ margin: '0', textAlign: 'center' }}>{data.outlet?.branch_title}</h2>
                        <h3 style={{ margin: '4px 0', textAlign: 'center' }}>{data.outlet?.org_name}</h3>
                        <p style={{ fontSize: '12px', textAlign: 'center' }}>
                            {data.outlet?.address1} {data.outlet?.address2}
                        </p>
                        <p style={{ fontSize: '12px', textAlign: 'center' }}>
                            <b>GSTIN:</b> {data.outlet?.gstin_no} | <b>CIN:</b> {data.outlet?.cin_no}
                        </p>
                    </div>

                    <div className="section-border flex-row" style={{ background: '#fcfcfc', borderLeft: '1px solid #000', borderRight: '1px solid #000' }}>
                        <div style={{ width: '30%' }}>
                            <small style={{ fontWeight: '700' }}><b>MOB:</b> {data.outlet?.mobile || data.outlet?.website_name}</small>
                        </div>
                        <div style={{ width: '40%', textAlign: 'center' }}>
                            <strong style={{ letterSpacing: '1px', fontWeight: '700' }}>{isPending ? 'ORDER FORM' : 'COMPOSITE TAX INVOICE'}</strong>
                        </div>
                        <div style={{ width: '30%' }} />
                    </div>

                    <div className="section-border flex-row" style={{ padding: '0' }}>
                        <div className="col-50" style={{ padding: '15px 20px', borderRight: '1px solid #000' }}>
                            <small style={{ color: '#666', fontWeight: '700' }}>BILL TO:</small>
                            <h4 style={{ margin: '6px 0', fontWeight: '700' }}>{data.customer?.company_name}</h4>
                            <p style={{ fontSize: '12px', fontWeight: '600' }}>
                                <b>Address :</b> {data.customer?.company_address}
                            </p>
                            <p style={{ fontSize: '12px', fontWeight: '600' }}>
                                <b>GSTIN : </b>
                                {data.customer?.gst_no}
                            </p>
                        </div>
                        <div className="col-50 text-right" style={{ padding: '15px 20px' }}>
                            <p style={{ fontWeight: '600' }}>
                                <b>{isPending ? 'Bill No:' : 'Invoice No:'}</b> {isPending ? data.header?.bill_no : data.header?.invoice_no || data.header?.bill_no}
                            </p>
                            <p style={{ fontWeight: '600' }}>
                                <b>{isPending ? 'Bill Date:' : 'Invoice Date:'}</b> {formatDate(data.header?.delivery_date)}
                            </p>
                            <p style={{ fontWeight: '600' }}>
                                <b>{isPending ? 'Bill Time:' : 'Invoice Time:'}</b> {data.header?.delivery_time}
                            </p>
                        </div>
                    </div>

                    <div className="table-section">
                        <table className="receipt-table">
                            <thead>
                                <tr>
                                    <th>Sl</th>
                                    <th style={{ textAlign: 'left' }}>Item Description</th>
                                    <th>HSN</th>
                                    <th>Qty</th>
                                    <th>Rate</th>
                                    <th className="text-right">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((item, idx) => (
                                    <tr key={idx}>
                                        <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                        <td>{item.product_name || item.item_name || item.productname || item.name || item.description || item.item_description || ''}</td>
                                        <td style={{ textAlign: 'center' }}>{item.hsn_code || item.hsn || item.hsn_no || item.hsncode || ''}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            {item.quantity} {item.uom}
                                        </td>
                                        <td className="text-right">{parseFloat(item.basic_rate).toFixed(2)}</td>
                                        <td className="text-right">{parseFloat(item.total_price).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="section-border flex-row">
                        <div className="col-50" style={{ padding: '12px 15px', verticalAlign: 'middle' }}>
                            <small>
                                <b>AMOUNT IN WORDS:</b>
                            </small>
                            <p style={{ fontStyle: 'italic', fontSize: '13px' }}>{getFormattedWords(totals.grand)}</p>
                        </div>
                        <div className="col-50" style={{ padding: '12px 15px', textAlign: 'right', borderLeft: '1px solid #000', verticalAlign: 'middle' }}>
                            <p>Taxable Amt: ₹{totals.taxable.toFixed(2)}</p>
                            <p>GST Amt: ₹{totals.taxAmt.toFixed(2)}</p>
                            <h3 style={{ marginTop: '8px', borderTop: '2px solid #000', paddingTop: '8px' }}>Grand Total: ₹{Math.round(totals.grand).toFixed(2)}</h3>
                        </div>
                    </div>

                    <div className="footer-text">This is a computer generated invoice and does not require a physical signature.</div>
                </div>
            </div>
        </div>
    );
};

export default OrderView;
